// A single normalised "lesson document" shape used by every view: library, studio, teach mode, print, email.
import type { CatalogLesson, Lesson } from "./catalog";
import { PRINCIPLE_BRIEFS } from "./catalog";
import type { StageKind } from "./constants";
import { EMPHASIS_LABELS, MODE_LABELS, TECH_LABELS } from "./constants";
import type { AdaptedLesson, AdaptParams } from "./schema";

export interface DocStage {
  kind: StageKind;
  name: string;
  minutes: number;
  teacher: string;
  students?: string;
  interaction?: string;
}

export interface LessonDoc {
  source: "handbook" | "adapted";
  lessonId: string;
  code: string;
  title: string;
  topic: string;
  level: string;
  duration: number;
  skill: string;
  objective: string;
  successCheck?: string;
  summary?: string;
  materials: string[];
  stages: DocStage[];
  support: string;
  stretch?: string;
  extension: string;
  l1Bridge?: string;
  teacherTalk?: string[];
  ledger?: { change: string; why: string; principle: string }[];
  kit?: { title: string; kind: string; items: string[] }[];
}

/** Scale stage minutes to sum exactly to `total` (largest-remainder rounding, min 1 min each). */
export function rebalanceMinutes<T extends { minutes: number }>(stages: T[], total: number): T[] {
  if (stages.length === 0) return stages;
  const raw = stages.map((s) => (Number.isFinite(s.minutes) && s.minutes > 0 ? s.minutes : 1));
  const sum = raw.reduce((a, b) => a + b, 0);
  const exact = raw.map((m) => (m / sum) * total);
  const out = exact.map((x) => Math.max(1, Math.floor(x)));
  let diff = total - out.reduce((a, b) => a + b, 0);
  const order = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r);
  for (let k = 0; diff !== 0 && k < 10_000; k++) {
    const idx = order[k % order.length].i;
    if (diff > 0) {
      out[idx]++;
      diff--;
    } else if (out[idx] > 1) {
      out[idx]--;
      diff++;
    }
  }
  return stages.map((s, i) => ({ ...s, minutes: out[i] }));
}

/**
 * Instant, deterministic re-timing (no AI). The handbook's standard 40-minute shape is
 * Warm-up 5 · Main 10–15 · Practice 10–15 · Wrap-up 5. Short periods compress with floors;
 * long periods grow Practice most and, from 55 minutes, add the lesson's Extension as a timed stage.
 * timelineShape() needs no lesson text, so the public catalogue can draw timelines too.
 */
export function timelineShape(total: number): DocStage[] {
  const t = Math.max(10, Math.min(120, Math.round(total)));
  const warm = clamp(Math.round(t * 0.125), 2, 8);
  const wrap = clamp(Math.round(t * 0.125), 2, 8);
  const core = t - warm - wrap;
  const withExt = t >= 55;
  const ext = withExt ? clamp(Math.round(core * 0.28), 8, 25) : 0;
  const main = Math.round((core - ext) * 0.5);
  const practice = core - ext - main;
  const stages: DocStage[] = [
    { kind: "warmup", name: "Warm-up", minutes: warm, teacher: "" },
    { kind: "main", name: "Main activity", minutes: main, teacher: "" },
    { kind: "practice", name: "Practice", minutes: practice, teacher: "" },
  ];
  if (withExt) stages.push({ kind: "extension", name: "Extension", minutes: ext, teacher: "" });
  stages.push({ kind: "wrapup", name: "Wrap-up", minutes: wrap, teacher: "" });
  return rebalanceMinutes(stages, t);
}

export function retimeStages(lesson: Lesson, total: number): DocStage[] {
  const text: Record<string, string> = {
    warmup: lesson.warmup, main: lesson.main, practice: lesson.practice, extension: lesson.extension, wrapup: lesson.wrapup,
  };
  return timelineShape(total).map((s) => ({ ...s, teacher: text[s.kind] ?? "" }));
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export function docFromLesson(lesson: Lesson, duration = lesson.duration): LessonDoc {
  return {
    source: "handbook",
    lessonId: lesson.id,
    code: lesson.id,
    title: lesson.activity,
    topic: lesson.topic,
    level: lesson.level,
    duration,
    skill: lesson.skill,
    objective: lesson.objective,
    materials: splitMaterials(lesson.materials),
    stages: retimeStages(lesson, duration),
    support: lesson.differentiation,
    extension: lesson.extension,
  };
}

export function docFromAdaptation(lesson: CatalogLesson, a: AdaptedLesson): LessonDoc {
  return {
    source: "adapted",
    lessonId: lesson.id,
    code: `${lesson.id}′`,
    title: a.title || lesson.activity,
    topic: lesson.topic,
    level: a.level,
    duration: a.duration,
    skill: a.skill || lesson.skill,
    objective: a.objective,
    successCheck: a.successCheck || undefined,
    summary: a.summary || undefined,
    materials: a.materials,
    stages: a.stages.map((s) => ({ ...s })),
    support: a.differentiation.support,
    stretch: a.differentiation.stretch || undefined,
    extension: a.extension,
    l1Bridge: a.l1Bridge || undefined,
    teacherTalk: a.teacherTalk.length ? a.teacherTalk : undefined,
    ledger: a.ledger.length ? a.ledger : undefined,
    kit: a.kit.length ? a.kit : undefined,
  };
}

export function splitMaterials(s: string): string[] {
  return s
    .replace(/\.$/, "")
    .split(/;\s*/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1));
}

export const DEFAULT_PARAMS: AdaptParams = {
  level: "B1",
  duration: 40,
  classSize: 35,
  mode: "in-person",
  tech: "board",
  context: "",
  interests: "",
  l1: "",
  emphasis: [],
  notes: "",
  includeKit: false,
};

export function paramsFromLesson(lesson: CatalogLesson): AdaptParams {
  const level = lesson.mixedLevel ? "B1" : (lesson.levelMin as AdaptParams["level"]);
  return { ...DEFAULT_PARAMS, level, duration: lesson.duration };
}

export function describeParams(p: AdaptParams): string {
  const bits = [
    p.level,
    `${p.duration} min`,
    p.mode === "one-to-one" ? "1 student" : `${p.classSize} students`,
    MODE_LABELS[p.mode],
    TECH_LABELS[p.tech],
  ];
  if (p.l1) bits.push(`L1 ${p.l1}`);
  if (p.context) bits.push(p.context);
  if (p.interests) bits.push(`into ${p.interests}`);
  for (const e of p.emphasis) bits.push(EMPHASIS_LABELS[e]);
  return bits.join(", ");
}

/** Plain-text rendering (email fallback, clipboard, print-free export). */
export function docToText(doc: LessonDoc): string {
  const lines: string[] = [];
  lines.push(`${doc.code}. ${doc.title}`, `${doc.topic}`, `${doc.level} | ${doc.duration} min | ${doc.skill}`, "");
  if (doc.summary) lines.push(doc.summary, "");
  lines.push(`OBJECTIVE: ${doc.objective}`);
  if (doc.successCheck) lines.push(`SUCCESS CHECK: ${doc.successCheck}`);
  lines.push("", `MATERIALS: ${doc.materials.join("; ")}`, "");
  for (const s of doc.stages) {
    lines.push(`${s.name.toUpperCase()} (${s.minutes} min)${s.interaction ? ` [${s.interaction}]` : ""}`);
    lines.push(`  Teacher: ${s.teacher}`);
    if (s.students) lines.push(`  Students: ${s.students}`);
  }
  lines.push("", `SUPPORT: ${doc.support}`);
  if (doc.stretch) lines.push(`STRETCH: ${doc.stretch}`);
  lines.push(`EXTENSION: ${doc.extension}`);
  if (doc.l1Bridge) lines.push(`L1 BRIDGE: ${doc.l1Bridge}`);
  if (doc.teacherTalk?.length) lines.push("", "TEACHER TALK:", ...doc.teacherTalk.map((t) => `  - ${t}`));
  if (doc.ledger?.length) {
    lines.push("", "WHAT CHANGED AND WHY:");
    for (const l of doc.ledger) lines.push(`  - ${l.change} — ${l.why} (Handbook §${l.principle})`);
  }
  if (doc.kit?.length) {
    lines.push("", "MATERIALS KIT:");
    for (const k of doc.kit) {
      lines.push(`  ${k.title}`);
      for (const it of k.items) lines.push(`    • ${it}`);
    }
  }
  return lines.join("\n");
}

export function principleBrief(id: string): string {
  return PRINCIPLE_BRIEFS[id] ?? "";
}
